import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { imageStyles, imageRatios, exampleImages } from '@/mocks/ai';

function ImagePanel() {
  const { user } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('photorealistic');
  const [ratio, setRatio] = useState('landscape');
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState<typeof exampleImages>([]);

  const handleGenerate = () => {
    const clean = prompt.trim();
    if (!clean || generating) return;
    setGenerating(true);
    setResults([]);
    setTimeout(() => {
      setResults(exampleImages);
      setGenerating(false);
    }, 1500);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-500/10">
            <i className="ri-image-ai-line text-xl text-primary-400" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground-50">
              Génération d'images
            </h3>
            <p className="text-xs text-foreground-400">
              Décrivez l'image, choisissez un style et générez.
            </p>
          </div>
        </div>
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-900 whitespace-nowrap">
          Mode démo
        </span>
      </div>

      <div className="space-y-4">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Ex : un logo moderne pour une startup tech, avec des formes géométriques dorées…"
          disabled={!user}
          rows={3}
          maxLength={500}
          className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-50 placeholder-foreground-600/50 focus:outline-none focus:border-primary-400 resize-none disabled:opacity-60"
        />

        <div>
          <p className="text-xs font-medium text-foreground-400 mb-2">Style</p>
          <div className="flex flex-wrap gap-2">
            {imageStyles.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStyle(s.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                  style === s.id
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-100 border border-background-200/70 text-foreground-50 hover:border-primary-400/40'
                }`}
              >
                <i className={s.icon} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-foreground-400 mb-2">Format</p>
          <div className="flex flex-wrap gap-2">
            {imageRatios.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRatio(r.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                  ratio === r.id
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-100 border border-background-200/70 text-foreground-50 hover:border-primary-400/40'
                }`}
              >
                <i className={r.icon} />
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleGenerate}
          disabled={!user || !prompt.trim() || generating}
          className="w-full px-5 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap text-sm"
        >
          {generating ? 'Génération…' : 'Générer l\'image'}
        </button>

        {generating && (
          <div className="flex items-center justify-center min-h-[160px]">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        )}

        {!generating && results.length > 0 && (
          <div>
            <div className="grid grid-cols-2 gap-3">
              {results.map((img) => (
                <div key={img.url} className="group relative overflow-hidden rounded-lg">
                  <img
                    src={img.url}
                    alt={img.label}
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <span className="text-xs text-white font-medium">{img.label}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-foreground-400 mt-3">
              Aperçu de démonstration. Connectez une clé IA pour la génération réelle à partir de votre prompt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImagePanel;